import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { baseConfig } from '@adornis/config/baseConfig.js';
import '@adornis/dragdrop/x-drag-drop.js';
import { CrDataWarning } from '@chancenwerk/client/components/chancen-component-data-warning.js';
import { ChromiumDialog } from '@chancenwerk/client/design/components/cr-dialog.js';
import { DurchfuehrungsTag } from '@chancenwerk/shared/_cms/products/entities/Product.js';
import { CourseAbschluss } from '@chancenwerk/shared/courses/CourseAbschluss.js';
import { type SchoolSubject, Weekday } from '@chancenwerk/enums/enums.js';
import '@chancenwerk/client/design/components/cr-flex.js';
import type { ChromiumFlex } from '@chancenwerk/client/design/components/cr-flex.js';
import '@chancenwerk/client/design/components/cr-grid.js';
import { CrPage } from '@chancenwerk/client/design/components/cr-page.js';
import '@chancenwerk/client/design/components/cr-progress-bar.js';
import type { KPF } from '@chancenwerk/shared/master-data-management/kpf/entities/KPF.js';
import type { ChancenwerkUser } from '@chancenwerk/shared/master-data-management/users/entities/ChancenwerkUser.js';
import type { Mitarbeiter } from '@chancenwerk/shared/master-data-management/users/entities/Mitarbeiter.js';
import { GraduationInformation, Student } from '@chancenwerk/shared/master-data-management/users/entities/Student.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { createRef, type Ref, ref } from 'lit/directives/ref.js';
import { repeat } from 'lit/directives/repeat.js';
import { DateTime } from 'luxon';
import { takeUntil, tap } from 'rxjs';
import '@chancenwerk/client/design/components/cr-nospace-toggle-content.js';
import '../components/board-component-course-aggregated-detail-card.js';
import '../components/board-component-course-detail-card.js';
import '../components/board-component-drag-drop.js';
import '../components/board-component-user-card.js';
import '../components/board-component-user-count-stats.js';
import '../components/board-component-user-pool-card.js';
import '../components/board-component-user-pool.js';
import { BoardDialogEditCourse, CourseProxy } from '../components/dialogs/board-dialog-edit-course.js';
import { createCourseDetailMixin } from '../course-detail-mixin.js';
import { dirtyData } from '../globalSubjects.js';
import { BoardRoutes } from '../routes.js';

const courseSelector: BaseQLSelectionSet<CourseAbschluss> = {
  _id: 1,
  _class: 1,
  kpfID: 1,
  kpf: { school: { name: 1 } },
  class: 1,
  location: 1,
  diKuLink: 1,
  endDate: 1,
  weekday: 1,
  startTime: 1,
  endTime: 1,
  leKoIDs: 1,
  osStudentIDs: 1,
  usStudentIDs: 1,
  schuKoIDs: 1,
  startDate: 1,
  plannedStartDate: 1,
  subject: 1,
  maxAttendanceCount: 1,
  openToAll: 1,
  type: 1,
  diKuIDs: 1,
  diKus: { _id: 1, _class: 1, vorname: 1, nachname: 1 },
  osStudents: {
    _id: 1,
    _class: 1,
    vorname: 1,
    nachname: 1,
    username: 1,
    guestAccount: 1,
    inactive: 1,
    email: 1,
    schuelertyp: 1,
    schoolInformation: { _class: 1, jahrgangsstufe: 1, zug: 1 },
    kpfs: { school: { name: 1 } },
    kuendigung: { austrittsdatum: 1 },
    graduationInformation: GraduationInformation.allFields,
  },
};

const studentSelector: BaseQLSelectionSet<Student> = {
  _id: 1,
  _class: 1,
  vorname: 1,
  username: 1,
  inactive: 1,
  nachname: 1,
  email: 1,
  guestAccount: 1,
  schuelertyp: 1,
  schoolInformation: { _class: 1, jahrgangsstufe: 1, zug: 1 },
  kuendigung: { austrittsdatum: 1 },
  kpfs: { school: { name: 1 } },
  graduationInformation: GraduationInformation.allFields,
  lernfoerderungDuerchfuehrungsTagChoosen: DurchfuehrungsTag.allFields,
};

const dikuSelector: BaseQLSelectionSet<Mitarbeiter> = {
  _id: 1,
  _class: 1,
  username: 1,
  vorname: 1,
  nachname: 1,
  employeeID: 1,
};

const kpfSelector: BaseQLSelectionSet<KPF> = {
  _id: 1,
  kpf: 1,
  schoolID: 1,
  school: { _class: 1, _id: 1, name: 1, kuerzel: 1 },
  schuKoIDs: 1,
  accessTokenForExternals: 1,
  accessTokenForStudents: 1,
  kuerzel: 1,
  schuKos: { _id: 1, _class: 1, vorname: 1, nachname: 1, email: 1, employeeID: 1 },
  leKos: { _id: 1, _class: 1 },
  produkt: {
    _class: 1,
    USLernFoerderung: 1,
    OSLernFoerderung: 1,
    uebungsLeiterProLernFoerderung: 1,
    kuerzel: 1,
    durchfuehrungsTage: DurchfuehrungsTag.allFields,
  },
};

/**
 * @customElement board-page-course-details-chancenabschluss
 */
@customElement('board-page-course-details-chancenabschluss')
export class BoardPageCourseDetailsChancenabschluss extends CrPage {
  override styles() {
    return [...super.styles(), { ':host': { height: '100%' } }];
  }

  constructor() {
    super();
    this.label = 'CHANCENabschluss';
    this.backRoute = BoardRoutes.overview;
  }

  @property() public kpfID = new RXController(this, '');
  @state() private readonly _details = createCourseDetailMixin(this, this.kpfID.observable, {
    courseClass: CourseAbschluss._class,
    selectionSets: { course: courseSelector, kpf: kpfSelector, os: studentSelector, diku: dikuSelector },
  });
  @state() private _filterFn: Maybe<(user: ChancenwerkUser) => boolean> = null;

  private readonly _scroll: Ref<ChromiumFlex> = createRef();

  override connectedCallback(): void {
    super.connectedCallback();

    this._details.kpf.observable
      .pipe(
        takeUntil(this.disconnected),
        tap(kpf => {
          this.label = `CHANCENabschluss ${kpf?.kuerzel ?? ''}`;
        }),
      )
      .subscribe();

    this._details.courses.observable.pipe(
      takeUntil(this.disconnected),
      tap(data => (data ? dirtyData.next(data) : dirtyData.next([]))),
    );
  }

  override header() {
    return html`
      <cr-flex space="md" horizontal crossaxis-center>
        <board-component-user-count-stats
          ?loading=${this._details.courses.isLoading || this._details.courseMap.isLoading}
          .elements=${{
            DiKus: this._details.users.dikus?.value?.length ?? 0,
            Jugendliche: this._details.users.os?.value?.length ?? 0,
          }}
        >
        </board-component-user-count-stats>
        <cr-button
          mode="action"
          @click=${async () => {
            await ChromiumDialog.waitFor(this._saveAll());
          }}
        >
          Speichern
        </cr-button>
      </cr-flex>
    `;
  }

  override content() {
    if (!A.isDevelopment && baseConfig.get('DEPLOYMENT') !== 'staging') {
      return nothing;
    }

    if (this._details.courses.isLoading || this._details.courseMap.isLoading) {
      return html`
        <cr-flex center crossaxis-center>
          <cr-loader></cr-loader>
        </cr-flex>
      `;
    }

    return html`
      <cr-flex space="sm" padding="xs" flex horizontal ${css({ minHeight: 0 })}>
        <cr-flex ${ref(this._scroll)} space="md" ${css({ flex: 4, overflowY: 'auto', paddingBottom: this.spacing.lg })}>
          ${repeat(
            this._details.courseMap.value?.keys() ?? [],
            k => k,
            location => {
              const course = this._details.courseMap.value?.get(location)?.at(0);
              return html`<board-component-course-aggregated-detail-card
                .expandable=${false}
                .dropzonesEnabled=${['DiKu', 'OS']}
                .coursesToManage=${course ? [course] : []}
                .kpf=${this._details.kpf.value}
                @dropped=${() => this.requestUpdate()}
                .actions=${[
                  {
                    icon: 'visibility',
                    tooltip: 'Passende jugendliche anzeigen',
                    action: () => {
                      this._filterFn = user => {
                        if (!(user instanceof Student) || !(course instanceof CourseAbschluss)) {
                          return false;
                        }

                        return !!(
                          !course.osStudentIDs?.includes(user._id) &&
                          user.graduationInformation?.type === course?.type &&
                          user.graduationInformation?.hasSubject((course?.subject ?? '') as SchoolSubject) &&
                          user.graduationInformation?.hasDayAvailable(
                            course?.weekday ?? Weekday.MONDAY,
                            course?.startTime ?? DateTime.now(),
                          )
                        );
                      };
                    },
                  },
                ]}
              ></board-component-course-aggregated-detail-card>`;
            },
          )}
          ${this._addCourseButton()}
        </cr-flex>
        ${this._userPool()}
      </cr-flex>
    `;
  }

  private _addCourseButton() {
    return html` <cr-card
      w100
      ${css({ background: this.colors.primary.shade(300), cursor: 'pointer' })}s
      center
      crossaxis-center
      @click=${async () => {
        await BoardDialogEditCourse.showPopup({
          props: {
            courseProxy: new CourseProxy({
              courses: [new CourseAbschluss({ kpfID: this._details.kpf.value?._id, kpf: this._details.kpf.value })],
            }),
            kpf: this._details.kpf.value,
            courseType: CourseAbschluss._class,
            new: true,
          },
        });
      }}
    >
      <cr-tooltip> <cr-text> Kurs hinzufügen </cr-text> </cr-tooltip>
      <cr-icon ${css({ color: 'white', fontSize: '2rem' })}>add</c-icon>
    </cr-card>`;
  }

  private _userPool() {
    return html` <board-component-user-pool-card width="600px">
      <x-flex
        slot="overTitle"
        ${css({ display: this._details.courses.value?.some(c => c.isDirty()) ? 'block' : 'none' })}
      >
        ${CrDataWarning(this._details.courses.value?.some(c => c.isDirty()))}
      </x-flex>

      <board-component-user-pool
        label="Jugendliche"
        .kpfID=${this.kpfID.value}
        .users=${this._details.users.os?.value ?? []}
        .userType=${'OS'}
        .filterFn=${this._filterFn}
        @filter-closed=${() => {
          this._filterFn = null;
          this.requestUpdate();
        }}
      >
      </board-component-user-pool>
      <board-component-user-pool
        label="digitale Kursleitungen"
        .users=${this._details.users.dikus?.value ?? []}
        .userType=${'DiKu'}
      >
      </board-component-user-pool>
    </board-component-user-pool-card>`;
  }

  private async _saveAll() {
    for (const c of this._details.courses.value ?? []) {
      await c.save();
    }
  }
}
